import type { Folder } from "~/types/api";

const STORAGE_KEY_EXPANDED = "sidebar-expanded-state";
const STORAGE_KEY_FOLDERS = "sidebar-folders-cache";
const CACHE_TTL = 60 * 60 * 1000;
let writesLocked = false;

type FolderCache = {
  data: Folder[];
  timestamp: number;
};

type FolderCacheMap = {
  [brandId: string]: FolderCache;
};

export const sidebarStorage = {
  saveExpandedState(expandedIds: Set<string>): void {
    if (writesLocked) return;
    try {
      sessionStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify([...expandedIds]));
    } catch (error) {
      console.error("Failed to save sidebar expanded state:", error);
    }
  },

  loadExpandedState(): Set<string> {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_EXPANDED);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        return new Set(parsed);
      }
    } catch (error) {
      console.error("Failed to load sidebar expanded state:", error);
    }
    return new Set<string>();
  },

  loadFolderCache(): FolderCacheMap {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_FOLDERS);
      if (stored) {
        return JSON.parse(stored) as FolderCacheMap;
      }
    } catch (error) {
      console.error("Failed to load folder cache:", error);
    }
    return {};
  },

  cacheFolders(brandId: number, folders: Folder[]): void {
    if (writesLocked) return;
    try {
      const cache = this.loadFolderCache();
      cache[brandId] = {
        data: folders,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY_FOLDERS, JSON.stringify(cache));
    } catch (error) {
      console.error("Failed to cache folders:", error);
    }
  },

  getCachedFolders(brandId: number): Folder[] | null {
    try {
      const brandCache = this.loadFolderCache()[brandId];

      if (!brandCache || Date.now() - brandCache.timestamp > CACHE_TTL) {
        return null;
      }

      return brandCache.data;
    } catch (error) {
      console.error("Failed to get cached folders:", error);
      return null;
    }
  },

  clearExpiredCache(): void {
    try {
      const cache = this.loadFolderCache();
      const now = Date.now();

      const expiredBrandIds = Object.entries(cache)
        .filter(([, brandCache]) => now - brandCache.timestamp > CACHE_TTL)
        .map(([brandId]) => brandId);

      if (expiredBrandIds.length > 0) {
        this.clearCachedState(expiredBrandIds);
      }
    } catch (error) {
      console.error("Failed to clear expired cache:", error);
    }
  },

  clearCachedState(brandIds: Array<string | number>): void {
    if (writesLocked) return;
    try {
      const expandedCache = this.loadExpandedState();
      const cache = this.loadFolderCache();

      for (const brandId of brandIds) {
        const id = String(brandId);
        delete cache[id];
        for (const key of expandedCache) {
          if (key === `brand-${id}` || key.startsWith(`folder-${id}-`)) {
            expandedCache.delete(key);
          }
        }
      }

      sessionStorage.setItem(STORAGE_KEY_FOLDERS, JSON.stringify(cache));
      this.saveExpandedState(expandedCache);
    } catch (error) {
      console.error("Failed to clear brand state:", error);
    }
  },

  lockWrites(): void {
    writesLocked = true;
  },

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY_EXPANDED);
      sessionStorage.removeItem(STORAGE_KEY_FOLDERS);
    } catch (error) {
      console.error("Failed to clear sidebar storage:", error);
    }
  },
};
